import { useState, useCallback, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { AlertCircle } from 'lucide-react';
import { patchMyTutorProfile } from '@/lib/tutor-api';
import { FooterButtons, type StepProps } from './shared';

export function ReviewStep({ profile, accessToken, onSaved, onError }: StepProps) {
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const submit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setBusy(true);
      try {
        const next = await patchMyTutorProfile(accessToken, {
          completedSteps: [...(profile?.completedSteps ?? []), 'review'],
          publishStatus: 'pending_review',
        });
        onSaved(next);
        navigate('/');
      } catch (err) {
        onError(err);
      } finally {
        setBusy(false);
      }
    },
    [profile?.completedSteps, accessToken, onSaved, onError, navigate],
  );

  if (!profile) {
    return (
      <p className="text-sm text-muted-foreground">
        Complete the earlier steps first — there's nothing to review yet.
      </p>
    );
  }

  const rows: { label: string; value: string; ok: boolean }[] = [
    { label: 'Display name', value: profile.displayName || '—', ok: !!profile.displayName },
    { label: 'Headline', value: profile.headline || '—', ok: !!profile.headline },
    { label: 'Subjects', value: profile.subjects.join(', ') || '—', ok: profile.subjects.length > 0 },
    { label: 'Grade levels', value: profile.gradeLevels.join(', ') || '—', ok: profile.gradeLevels.length > 0 },
    {
      label: 'Availability',
      value:
        profile.availability.length > 0
          ? `${profile.availability.length} time block${profile.availability.length === 1 ? '' : 's'} · ${profile.homeTz}`
          : 'Missing',
      ok: profile.availability.length > 0,
    },
    {
      label: 'Hourly rate',
      value: `$${profile.hourlyRate.usdMinor / 100}/hr`,
      ok: profile.hourlyRate.usdMinor > 0,
    },
    { label: 'Profile photo', value: profile.photoUrl ? 'Uploaded ✓' : 'Missing', ok: !!profile.photoUrl },
    {
      label: 'Bank details',
      value: profile.bankDetailsOnFile ? 'On file ✓' : 'Optional (Not added)',
      ok: true,
    },
  ];

  const allGood = rows.every((r) => r.ok);

  return (
    <form className="space-y-5" onSubmit={submit}>
      <ul className="divide-y divide-slate-100 overflow-hidden rounded-xl border border-slate-200 dark:divide-slate-700 dark:border-slate-700">
        {rows.map((row) => (
          <li
            key={row.label}
            className="flex items-center justify-between gap-4 px-4 py-3 text-sm dark:bg-slate-800/20"
          >
            <span className="text-muted-foreground">{row.label}</span>
            <span className={`text-right font-medium ${row.ok ? 'text-foreground' : 'text-red-500 dark:text-red-400'}`}>
              {row.value}
            </span>
          </li>
        ))}
      </ul>

      {!allGood && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          Some sections are incomplete. Please go back and fill them in before submitting.
        </div>
      )}

      <p className="text-sm text-muted-foreground">
        Submitting sends your profile to our review queue. We'll publish it as soon as the
        admin team approves — usually within one business day.
      </p>

      <FooterButtons primaryLabel="Submit for review" busy={busy} />
    </form>
  );
}
